import {
	type Interval,
	addMilliseconds,
	differenceInMilliseconds,
	isAfter,
	isWithinInterval,
	subDays,
	subHours,
	subMilliseconds,
	subMinutes,
	subMonths,
	toDate,
} from "@suse-o11y/date-time";
import { formatInterval } from "@suse-o11y/date-time/format/format-interval";
import type { Time } from "@suse-o11y/date-time/time";
import { min } from "@suse-o11y/min";
import capitalize from "lodash/capitalize";
import isUndefined from "lodash/isUndefined";

export const TIME_RANGE_LIMIT = 15_552_000_000; // 6 months (every month is 30 days)

export interface TimeRange {
	start(now?: Time): Date;
	readonly end: Date | undefined; // if undefined - that means that this TimeRange is not limited and bound with LIVE mode
	endDate(now?: Time): Date;
	/**
	 * Returns duration in milliseconds.
	 */
	duration(): number;
	toString(): string;
	toUserFriendlyString(): string;
	readonly isCustom: boolean;
	isUntilNow(): boolean;
	contains(timestamp: Time): boolean;
	toJSON(): any;
	isSpecific(): this is SpecificRange;
	toSpecific(now?: Time): SpecificRange;
	toInterval(now?: Time): Interval;
	isEqual(timeRange: TimeRange): boolean;
	isWithinLimit(): boolean;
	isLessLimit(): boolean;
	isInFuture(now?: Time): boolean;
	zoomOut(now?: Time): TimeRange;
	shiftLeft(now?: Time): SpecificRange;
	shiftRight(now?: Time): SpecificRange;
}

export const getStartTimestamp = (timeRange: TimeRange): number => timeRange.start().valueOf() as number;

export const getEndTimestamp = (timeRange: TimeRange): number | undefined =>
	timeRange.end?.valueOf() as number | undefined;

export const getCurrentEndTimestamp = (timeRange: TimeRange): number =>
	(timeRange.end ?? new Date()).valueOf() as number;

const commonTimeRange: TimeRange = {
	start() {
		return new Date();
	},

	endDate(now) {
		return now == null ? new Date() : toDate(now);
	},

	duration() {
		return differenceInMilliseconds(this.endDate(0) as Time, this.start(0) as Time);
	},

	end: undefined,
	isCustom: false,

	isUntilNow(): boolean {
		return isUndefined(this.end);
	},

	contains(timestamp: Time): boolean {
		return safeContainsDate(timestamp, this.start(), this.endDate());
	},

	toJSON(): unknown {
		return {
			start: this.start(),
			end: this.end ?? "until now",
			isCustom: this.isCustom,
		};
	},

	toUserFriendlyString(): string {
		return capitalize(this.toString().toLowerCase());
	},

	isSpecific(): this is SpecificRange {
		return false;
	},

	toSpecific(now: Time = Date.now()) {
		return new SpecificRange(this.start(now), now);
	},

	toInterval(now: Time = Date.now()) {
		return {
			start: this.start(now),
			end: now,
		};
	},

	isEqual(timeRange: TimeRange): boolean {
		return timeRange.toString() === this.toString();
	},

	isWithinLimit(): boolean {
		return true;
	},

	isLessLimit(): boolean {
		return true;
	},

	isInFuture(): boolean {
		return false;
	},

	zoomOut(now: Time = Date.now()) {
		const currentTimeRangeIndex = STATIC_TIME_RANGES.findIndex((timeRange) => this.isEqual(timeRange));
		const nextTimeRange = STATIC_TIME_RANGES.at(currentTimeRangeIndex + 1);

		if (nextTimeRange != null) {
			return nextTimeRange;
		}

		return this.toSpecific(now).zoomOut(now);
	},

	shiftLeft(now: Time = Date.now()) {
		return this.toSpecific(now).shiftLeft();
	},

	shiftRight(now: Time = Date.now()) {
		return this.toSpecific(now).shiftRight(now);
	},
};

export const LAST_5_MINUTES: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subMinutes(now, 5);
	},

	toString() {
		return "LAST 5 MINUTES";
	},
};

export const LAST_15_MINUTES: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subMinutes(now, 15);
	},

	toString() {
		return "LAST 15 MINUTES";
	},
};
export const LAST_30_MINUTES: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subMinutes(now, 30);
	},

	toString() {
		return "LAST 30 MINUTES";
	},
};

export const LAST_1_HOUR: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subHours(now, 1);
	},

	toString() {
		return "LAST 1 HOUR";
	},
};
export const LAST_3_HOURS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subHours(now, 3);
	},

	toString() {
		return "LAST 3 HOURS";
	},
};

export const LAST_6_HOURS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subHours(now, 6);
	},

	toString() {
		return "LAST 6 HOURS";
	},
};
const LAST_12_HOURS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subHours(now, 12);
	},

	toString() {
		return "LAST 12 HOURS";
	},
};

export const LAST_24_HOURS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subDays(now, 1);
	},

	toString() {
		return "LAST 24 HOURS";
	},
};
export const LAST_2_DAYS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subDays(now, 2);
	},

	toString() {
		return "LAST 2 DAYS";
	},
};

export const LAST_7_DAYS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subDays(now, 7);
	},

	toString() {
		return "LAST 7 DAYS";
	},
};

export const LAST_30_DAYS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subDays(now, 30);
	},

	toString() {
		return "LAST 30 DAYS";
	},
};

export const LAST_6_MONTHS: TimeRange = {
	...commonTimeRange,

	start(now = Date.now()) {
		return subMonths(now, 6);
	},

	toString() {
		return "LAST 6 MONTHS";
	},
};

export class SpecificRange implements TimeRange {
	public readonly isCustom: boolean = true;

	private readonly rangeStart: Date;
	/**
	 * TODO STAC-22111 make required
	 */
	public end: Date | undefined;
	private readonly label?: string;

	constructor(rangeStart: Time, end: Time | undefined, label?: string) {
		this.rangeStart = toDate(rangeStart);
		this.end = isUndefined(end) ? undefined : toDate(end);
		this.label = label;
	}

	start() {
		return this.rangeStart;
	}

	endDate(now?: Time): Date {
		return this.end ?? (now == null ? new Date() : new Date(now));
	}

	duration(): number {
		return differenceInMilliseconds(this.endDate(), this.start());
	}

	isUntilNow() {
		return isUndefined(this.end);
	}

	contains(timestamp: Time): boolean {
		return safeContainsDate(timestamp, this.start(), this.endDate());
	}

	toJSON() {
		return {
			start: this.start(),
			end: this.end ?? "until now",
			isCustom: this.isCustom,
		};
	}

	toString() {
		if (this.label) {
			return this.label;
		}
		return formatInterval([this.rangeStart, this.end]).toString();
	}

	toUserFriendlyString(): string {
		return this.toString();
	}

	isSpecific(): this is SpecificRange {
		return true;
	}

	toSpecific(): SpecificRange {
		return this;
	}

	toInterval(): Interval {
		return {
			start: this.start(),
			end: this.endDate(),
		};
	}

	isEqual(timeRange: TimeRange): boolean {
		return timeRange.start().valueOf() === this.start().valueOf() && timeRange.end?.valueOf() === this.end?.valueOf();
	}

	isWithinLimit(): boolean {
		return this.duration() <= TIME_RANGE_LIMIT;
	}

	isLessLimit(): boolean {
		return this.duration() < TIME_RANGE_LIMIT;
	}

	isInFuture(now: Time = Date.now()): boolean {
		return isAfter(this.endDate(now), now);
	}

	/**
	 * Doubles the TimeRange's duration until TIME_RANGE_LIMIT is reached.
	 */
	zoomOut(now: Time = Date.now()): SpecificRange {
		if (!this.isLessLimit()) {
			return this;
		}

		const duration = this.duration();
		const nextDuration = min(TIME_RANGE_LIMIT, 2 * duration);
		const shiftEnd = (nextDuration - duration) / 2;

		// zoom out the end at half of duration until now time
		const zoomedOutEnd = min(now, addMilliseconds(this.endDate(now), shiftEnd));
		// zoom out the start twice the current duration from the new end
		const zoomedOutStart = subMilliseconds(zoomedOutEnd, nextDuration);

		return new SpecificRange(zoomedOutStart, zoomedOutEnd);
	}

	shiftLeft(): SpecificRange {
		const start = this.start();

		return new SpecificRange(subMilliseconds(start, this.duration()), start);
	}

	shiftRight(now: Time = Date.now()): SpecificRange {
		const end = this.endDate(now);

		return new SpecificRange(end, addMilliseconds(end, this.duration()));
	}
}

export const createSpecificRange = (start: Time, end?: Time): SpecificRange => new SpecificRange(start, end);

const safeContainsDate = (timestamp: Time, start: Date, end: Date): boolean => {
	try {
		return isWithinInterval(timestamp, { start, end });
	} catch {
		return false;
	}
};

export const getStaticTimeRange = (serializingTimeRange: string): TimeRange | undefined =>
	STATIC_TIME_RANGES.find((tr) => tr.toString().replaceAll(" ", "_") === serializingTimeRange);

export const fromSerializingTimeRange = (serializingTimeRange: SerializingTimeRange): TimeRange => {
	const staticTimeRange = getStaticTimeRange(serializingTimeRange as string);
	const customSerializedTimeRange = serializingTimeRange as SerializingCustomTimeRange;

	return staticTimeRange ?? new SpecificRange(customSerializedTimeRange.start, customSerializedTimeRange.end);
};

export const toSerializingTimeRange = (timeRange: TimeRange): SerializingTimeRange => {
	const timeRangeString = timeRange.toString();
	const staticTimeRange = STATIC_TIME_RANGES.find((tr) => tr.toString() === timeRangeString);

	return (
		(staticTimeRange?.toString().replaceAll(" ", "_") as SerializingStaticTimeRange) ?? {
			start: timeRange.start(),
			end: timeRange.end,
		}
	);
};

const STATIC_TIME_RANGE_KEYS: Array<SerializingStaticTimeRange> = [
	"LAST_5_MINUTES",
	"LAST_15_MINUTES",
	"LAST_30_MINUTES",
	"LAST_1_HOUR",
	"LAST_3_HOURS",
	"LAST_6_HOURS",
	"LAST_12_HOURS",
	"LAST_24_HOURS",
	"LAST_2_DAYS",
	"LAST_7_DAYS",
	"LAST_30_DAYS",
];

export const isStaticTimeRangeKey = (value: string): value is SerializingStaticTimeRange =>
	STATIC_TIME_RANGE_KEYS.includes(value as SerializingStaticTimeRange);

export const STATIC_TIME_RANGES: Array<TimeRange> = [
	LAST_5_MINUTES,
	LAST_15_MINUTES,
	LAST_30_MINUTES,
	LAST_1_HOUR,
	LAST_3_HOURS,
	LAST_6_HOURS,
	LAST_12_HOURS,
	LAST_24_HOURS,
	LAST_2_DAYS,
	LAST_7_DAYS,
	LAST_30_DAYS,
];

export type SerializingStaticTimeRange =
	| "LAST_5_MINUTES"
	| "LAST_15_MINUTES"
	| "LAST_30_MINUTES"
	| "LAST_1_HOUR"
	| "LAST_3_HOURS"
	| "LAST_6_HOURS"
	| "LAST_12_HOURS"
	| "LAST_24_HOURS"
	| "LAST_2_DAYS"
	| "LAST_7_DAYS"
	| "LAST_30_DAYS";

interface SerializingCustomTimeRange {
	start: Date;
	end?: Date;
}

export type SerializingTimeRange = SerializingStaticTimeRange | SerializingCustomTimeRange;

/**
 * clamp the timeRange to the 6 months limit, it takes the end date of the current timeRange and subtracts the
 * TIME_RANGE_LIMIT from it.
 * @param timeRange - timeRange to clamp
 * @return the new timeRange clamped to 6 months limit
 */
export const adjustTimeRangeToHalfYearLimit = (timeRange: TimeRange): SpecificRange => {
	const endDate = timeRange.endDate();
	const startDate = endDate.valueOf() - TIME_RANGE_LIMIT;
	return createSpecificRange(startDate, endDate);
};

export const DEFAULT_TIME_RANGE_KEY: SerializingStaticTimeRange = "LAST_1_HOUR";
